import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { Button } from '../ui/button';
import { LogOut, LayoutDashboard, MessageCircle, BookOpen, PenLine, MapPin, Menu, X, Sparkles, Users, UserRound, Video, ChevronRight } from 'lucide-react';
import { useState } from 'react';

// Stylized Clair name: CLaiR with lowercase "ai" in turquoise
export const ClairName = ({ className = "" }) => (
  <span className={className}>CL<span className="text-primary lowercase">ai</span>R</span>
);

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Desktop nav items (flat list)
  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/coach', label: 'Clair', icon: MessageCircle, isClairLabel: true },
    { to: '/journal', label: 'Journal', icon: PenLine },
    { to: '/goals', label: 'Goals', icon: MapPin },
    { to: '/content', label: 'Library', icon: BookOpen },
    { to: '/groups', label: 'Groups', icon: Users },
    { to: '/reflection', label: 'Insights', icon: Sparkles },
    { to: '/coaching', label: '1-on-1', icon: Video },
  ];

  // Mobile nav items (grouped)
  const mobileNavGroups = [
    {
      title: 'My Journey',
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/journal', label: 'Journal', icon: PenLine },
        { to: '/goals', label: 'Goals', icon: MapPin },
        { to: '/reflection', label: 'Insights', icon: Sparkles },
      ]
    },
    {
      title: 'Connect',
      items: [
        { to: '/coach', label: 'CLaiR', icon: MessageCircle, isClairLabel: true },
        { to: '/groups', label: 'Groups', icon: Users },
        { to: '/coaching', label: '1-on-1 Coaching', icon: Video },
      ]
    },
    {
      title: 'Learn',
      items: [
        { to: '/content', label: 'Content Library', icon: BookOpen },
      ]
    },
  ];

  const handleNavClick = (to) => {
    setMobileMenuOpen(false);
    navigate(to);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <nav className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-lg sm:text-xl font-serif text-foreground">
              <span className="hidden sm:inline">ClarityPath <span className="text-muted-foreground">—</span> <span className="text-accent">Guided by Mo</span></span>
              <span className="sm:hidden text-accent">ClarityPath</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-2 lg:gap-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-1.5 text-sm font-medium duration-300 px-2 lg:px-3 py-2 rounded-full ${
                      isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="hidden lg:inline">
                      {item.isClairLabel ? <ClairName /> : item.label}
                    </span>
                  </Link>
                );
              })}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-muted transition-colors cursor-pointer"
                data-testid="user-profile-btn"
                title={user?.name || 'Profile'}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                  {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
                </div>
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="rounded-full"
              >
                <LogOut size={16} />
              </Button>
            </div>

            {/* Mobile: User Initial + Menu Toggle */}
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={() => navigate('/settings')}
                className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary"
                data-testid="mobile-profile-btn"
              >
                {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
              </button>
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[57px] z-40 bg-background">
          <div className="p-4 space-y-6 overflow-y-auto h-full pb-24">
            {/* Grouped Navigation */}
            {mobileNavGroups.map((group) => (
              <div key={group.title}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                  {group.title}
                </p>
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.to;
                    return (
                      <button
                        key={item.to}
                        onClick={() => handleNavClick(item.to)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                          isActive 
                            ? 'bg-primary/10 text-primary' 
                            : 'hover:bg-muted text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={20} />
                          <span className="font-medium">
                            {item.isClairLabel ? <ClairName /> : item.label}
                          </span>
                        </div>
                        <ChevronRight size={18} className="text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Profile & Logout */}
            <div className="border-t border-border pt-4">
              <button
                onClick={() => handleNavClick('/settings')}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-muted"
              >
                <div className="flex items-center gap-3">
                  <UserRound size={20} />
                  <span className="font-medium">Profile & Settings</span>
                </div>
                <ChevronRight size={18} className="text-muted-foreground" />
              </button>
              
              <Button 
                variant="outline" 
                onClick={handleLogout} 
                className="w-full mt-4 rounded-full"
              >
                <LogOut size={16} className="mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {children}
      </main>
    </div>
  );
};
